import { AuthError, signUp } from "aws-amplify/auth";
import { motion } from "framer-motion";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { PublicInvitation } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  YourDetailsFormData,
  yourDetailsSchema,
} from "../types/your-details.schema";

import { PasswordRules } from "./password-rules";

type YourDetailsFormProps = {
  invitation?: PublicInvitation;
};

export function YourDetailsForm({ invitation }: YourDetailsFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email?.toLowerCase().trim();
  const form = useForm<YourDetailsFormData>({
    resolver: zodResolver(yourDetailsSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      hasAcceptedTerms: false,
      isSubscribedToMarketing: false,
      email: invitation ? invitation.email.toLowerCase().trim() : email,
    },
  });
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  async function onSubmit(values: YourDetailsFormData) {
    const lowerCaseEmail = values.email.toLowerCase().trim();
    const organisations: string[] = invitation
      ? Object.keys(invitation.organisations)
      : [];

    try {
      //TODO: move it to registration/api
      const response = await signUp({
        username: lowerCaseEmail,
        password: values.password,
        options: {
          userAttributes: {
            email: lowerCaseEmail,
            given_name: values.firstName,
            family_name: values.lastName,
            "custom:isMarketing": values.isSubscribedToMarketing
              ? "true"
              : "false",
            "custom:organisations": organisations.join(","),
            ...(invitation ? { "custom:invitationId": invitation.id } : {}),
          },
          autoSignIn: true,
        },
      });

      if (response.isSignUpComplete) {
        return navigate("/sign-in");
      }

      if (response.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        return navigate("/sign-up/confirm-email", {
          state: { email: lowerCaseEmail },
        });
      }
    } catch (error) {
      if (error instanceof AuthError) {
        console.error("Auth error:", error.message);
        switch (error.name) {
          case "UsernameExistsException":
            form.setError("email", { message: "This email is already in use" });
            break;
          case "InvalidPasswordException":
            form.setError("password", { message: "Invalid password format" });
            break;
          default:
            form.setError("root", {
              message: "An error occurred during sign up. Please try again.",
            });
        }
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>Email</FormLabel>
                <FormControl>
                  <Input type="email" disabled {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div>
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Password</FormLabel>
                  <div className="relative flex">
                    <FormControl>
                      <Input
                        type={isPasswordVisible ? "text" : "password"}
                        placeholder="••••••"
                        {...field}
                        onFocus={() => setIsPasswordFocused(true)}
                        onBlur={() => setIsPasswordFocused(false)}
                        className="pr-8"
                      />
                    </FormControl>

                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className="absolute right-0 top-1/2 -translate-y-1/2 hover:bg-transparent"
                      onClick={() => {
                        setIsPasswordFocused(true);
                        setIsPasswordVisible(!isPasswordVisible);
                      }}
                    >
                      {isPasswordVisible ? (
                        <EyeOff className="size-4" aria-label="Hide password" />
                      ) : (
                        <Eye className="size-4" aria-label="Show password" />
                      )}
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <motion.div
              className="overflow-hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={
                isPasswordFocused
                  ? { opacity: 1, height: "auto" } // 224px is equivalent to h-56
                  : { opacity: 0, height: 0 }
              }
              transition={{ duration: 0.3 }}
            >
              <PasswordRules
                password={form.watch("password")}
                schema={yourDetailsSchema}
              />
            </motion.div>
          </div>
          <FormField
            control={form.control}
            name="isSubscribedToMarketing"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Marketing Communications</FormLabel>
                  <FormDescription>
                    I would like to OPT IN to receive marketing communication on
                    the ClassView service. I understand if I do not opt in, I
                    will still receive service communications such as feature
                    updates and service improvements.
                  </FormDescription>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="hasAcceptedTerms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel isRequired>End User Agreement (EULA)</FormLabel>
                  <FormDescription>
                    I agree to End User Agreement (EULA) as set out in our{" "}
                    <a
                      href="https://example.com"
                      className={cn(
                        buttonVariants({
                          variant: "link",
                          className: "h-auto p-0",
                        }),
                      )}
                    >
                      Privacy Policy.
                    </a>
                  </FormDescription>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          {form.formState.errors.root && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unexpected error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root.message}
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-between gap-4">
            <Button
              type="submit"
              className="ml-auto"
              isLoading={form.formState.isSubmitting}
              isAnimated
            >
              Continue
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
